/**
 * Brand Plane Bridge — ThemeConfig.token merge ONLY (§12-D · AP-BRAND-01)
 * @see docs/inflomatrix-constitution/ui-architecture/BRAND_PLANE_BRIDGE.md
 */

import type { ThemeConfig } from 'antd';
import type { BrandIdentity } from '@shared/site/design/brand-identity';
import { parseCssColor } from '@shared/site/design/brand-identity';
import {
  allowedAntdTokensForLevel,
  isForbiddenBusinessBrandToken,
} from '@shared/site/design/brand-injection-tier-registry';

function adjustAccentTone(accent: string, direction: 'lighter' | 'darker'): string {
  const parsed = parseCssColor(accent);
  if (!parsed) {
    return accent;
  }
  const factor = direction === 'lighter' ? 1.12 : 0.88;
  return `#${[parsed.r, parsed.g, parsed.b]
    .map((channel) =>
      Math.min(255, Math.max(0, Math.round(channel * factor)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;
}

export interface BusinessBrandThemeMerge {
  token: NonNullable<ThemeConfig['token']>;
  components?: ThemeConfig['components'];
}

/** Map BrandIdentity → Ant Design token merge (allowlisted L1/L2 only) */
export function mapBrandToBusinessThemeTokens(identity: BrandIdentity): BusinessBrandThemeMerge {
  const allowed = new Set(allowedAntdTokensForLevel(identity.injectionLevel));

  const candidateToken: Record<string, string | number> = {
    colorPrimary: identity.accent,
    colorLink: identity.accent,
    colorLinkHover: adjustAccentTone(identity.accent, 'lighter'),
    colorLinkActive: adjustAccentTone(identity.accent, 'darker'),
    colorTextLightSolid: identity.accentOn,
  };

  if (identity.injectionLevel === 'L2') {
    if (identity.fontBody) {
      candidateToken.fontFamily = identity.fontBody;
    }
    if (identity.radiusPx !== undefined) {
      candidateToken.borderRadius = identity.radiusPx;
      candidateToken.borderRadiusLG = Math.min(identity.radiusPx + 4, 16);
    }
  }

  const token: NonNullable<ThemeConfig['token']> = {};
  for (const [key, value] of Object.entries(candidateToken)) {
    if (isForbiddenBusinessBrandToken(key)) {
      continue;
    }
    if (!allowed.has(key)) {
      continue;
    }
    (token as Record<string, string | number>)[key] = value;
  }

  return {
    token,
    components: {
      Button: {
        colorPrimary: identity.accent,
        colorTextLightSolid: identity.accentOn,
      },
      Menu: {
        itemSelectedColor: identity.accent,
        darkItemSelectedColor: identity.accent,
      },
    },
  };
}
